<script lang="ts">
import { defineComponent, h, mergeProps, onMounted, ref, unref, watch } from 'vue';
import { paramCase } from 'change-case';

import { classNames, omit } from '@components/utils';

import {
  makeDelayProps,
  makeDimensionProps,
  makeLazyProps,
  makeLocaleProps,
  makeOverlayProps,
  makeStatusProps,
  makeTagProps,
  makeTextProps,
  makeThemeProps,
  makeTitleProps,
  makeTransitionProps,
  makeVariantProps,
  provideTheme,
  useProxyModel,
  useRender,
  useScopeId
} from '../../composable';
import { CdsOverlay } from '../CdsOverlay';
import { makeActivatorProps, makeLocationStrategyProps, useLocation } from '../CdsOverlay/composable';

import CdsNotification from './CdsNotification.vue';
import { makeNotificationProps } from './composable';

import type { PropType } from 'vue';

const WRAPPER_SELECTOR = 'cds-notification-alert__wrapper';
export const componentName = 'CdsNotificationAlert';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Время (мс) через которое уведомление будет закрыто
     */
    timeout: {
      type: [Number, String] as PropType<string | number>,
      default: 5000
    },

    ...makeTitleProps(),
    ...makeTextProps(),
    ...makeNotificationProps(),
    ...makeVariantProps(),
    ...makeStatusProps(),
    ...makeTagProps(),
    ...makeLocaleProps(),
    ...makeOverlayProps(),
    ...makeActivatorProps(),
    ...makeLazyProps(),
    ...makeDelayProps(),
    ...makeDimensionProps(),
    ...makeThemeProps(),
    ...makeLocationStrategyProps({
      location: 'top' as const
    }),
    ...makeTransitionProps({
      transition: 'notification-transition'
    })
  },
  emits: {
    'update:modelValue': (value: boolean) => true
  },
  setup(props, { slots, emit, expose }) {
    const isActive = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { locationStyles } = useLocation(props, false, undefined, WRAPPER_SELECTOR);
    const { scopeId } = useScopeId();

    const overlay = ref<typeof CdsOverlay>();

    watch(isActive, startTimeout);
    watch(() => props.timeout, startTimeout);

    onMounted(() => {
      if (unref(isActive)) startTimeout();
    });

    let activeTimeout = -1;
    function startTimeout() {
      const timeout = Number(props.timeout);

      if (timeout === 0) return;

      window.clearTimeout(activeTimeout);

      if (!unref(isActive) || timeout === -1) return;

      activeTimeout = window.setTimeout(() => {
        isActive.value = false;
      }, timeout);
    }

    function onPointerenter() {
      window.clearTimeout(activeTimeout);
    }

    useRender(() => {
      const overlayProps = omit(props, [
        'tag',
        'timeout',
        'variant',
        'status',
        'text',
        'title',
        'icon',
        'customIcon',
        'closable',
        'closeLabel',
        'closeIcon',
        'locale'
      ]);

      return h(
        CdsOverlay,
        {
          class: classNames(paramCase(componentName), unref(themeClasses), {
            'cds-notification-alert--active': unref(isActive)
          }),
          ...overlayProps,
          modelValue: unref(isActive),
          'onUpdate:modelValue': (value: boolean) => {
            isActive.value = value;
            emit('update:modelValue', value);
          },
          contentProps: mergeProps(
            {
              class: classNames(WRAPPER_SELECTOR),
              style: unref(locationStyles),
              'data-alert': WRAPPER_SELECTOR,
              onPointerenter,
              onPointerleave: startTimeout
            },
            overlayProps.contentProps
          ),
          persistent: true,
          scrim: false,
          noClickAnimation: true,
          scrollStrategy: 'none',
          ...scopeId
        },
        {
          activator: slots.activator,
          default: () =>
            h(
              CdsNotification,
              {
                title: props.title,
                text: props.text,
                variant: props.variant,
                status: props.status,
                tag: props.tag,
                locale: props.locale,
                icon: props.icon,
                customIcon: props.customIcon,
                closable: props.closable,
                closeLabel: props.closeLabel,
                closeIcon: props.closeIcon,
                'onClick:close': () => {
                  isActive.value = false;
                  emit('update:modelValue', false);
                }
              },
              {
                title: slots.title,
                default: slots.default,
                text: slots.text,
                prepend: slots.prepend,
                append: slots.append,
                close: slots.close,
                actions: slots.actions
              }
            )
        }
      );
    });

    expose({
      overlay
    });

    return {};
  }
});
</script>
